import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Angulartics2Segment } from 'angulartics2';
import { deepmerge } from 'deepmerge-ts';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { Direction } from '@angular/cdk/bidi';
import { MessageBoxComponent } from '@shared/shared/components/message-box/message-box.component';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { IMemberProfilePaymentCard } from '@shared/shared/interfaces/member-profile-payment-card.interfaces';
import * as MemberProfilePaymentCardsActions from '../actions/member-profile-payment-cards.actions';
import { MemberProfilePaymentCardAction } from '../actions/member-profile-payment-cards.actions';
import { MemberProfileState } from '../reducers';
import { getMemberProfilePaymentCardsFeatureState } from '../selectors';

@Injectable()
export class MemberProfilePaymentCardEffects {
  constructor(
    private actions$: Actions<MemberProfilePaymentCardAction>,
    private store: Store<MemberProfileState>,
    private http: HttpClient,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private angulartics2Segment: Angulartics2Segment
  ) {}

  public getPaymentCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfilePaymentCardsActions.GET_PROFILE_PAYMENT_CARDS),
      switchMap(() => {
        return this.http
          .get<IMemberProfilePaymentCard[]>(ApiEndpoint.MEMBER_PROFILE_PAYMENT_CARD, {
            params: { online_use: 'true', offline_use: 'false' },
          })
          .pipe(
            map((cards) => new MemberProfilePaymentCardsActions.GetProfilePaymentCardsSuccess(cards)),
            catchError((error) => of(new MemberProfilePaymentCardsActions.GetProfilePaymentCardsFail(error)))
          );
      })
    )
  );

  public createProfilePaymentCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfilePaymentCardsActions.CREATE_PROFILE_PAYMENT_CARD),
      withLatestFrom(this.store.select(getMemberProfilePaymentCardsFeatureState)),
      tap(([action, cards]) => {
        let preferredCardCheckbox = 'Default';
        if (cards.length) {
          preferredCardCheckbox = action.payload.is_default_card ? 'Yes' : 'No';
        }

        const params = {
          preferredCardCheckbox,
          // Temporarily removing offline use tracking until we can save cards for offline use
          // securelyStoreCardCheckbox: action.payload.offline_use ? 'Yes' : 'No',
        };

        this.angulartics2Segment.eventTrack('Add new card - Submit button clicked', params);
      }),
      switchMap(([action]) => {
        return this.http.post<IMemberProfilePaymentCard>(ApiEndpoint.MEMBER_PROFILE_PAYMENT_CARD, action.payload).pipe(
          map(() => new MemberProfilePaymentCardsActions.GetProfilePaymentCards()),
          catchError(() => of(new MemberProfilePaymentCardsActions.CreateProfilePaymentCardFail()))
        );
      })
    )
  );

  public createProfilePaymentCardFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MemberProfilePaymentCardsActions.CREATE_PROFILE_PAYMENT_CARD_FAIL),
        tap(() => {
          // TODO: different texts for different errors ('StoredCardsError:CardExist')
          const text = this.translateService.instant('StoredCardsError:CardNotSaved');

          this.angulartics2Segment.eventTrack('Stored card - Error could not save', {});
          this.dialog.open(MessageBoxComponent, {
            direction: document.querySelector('html')?.dir as Direction,
            data: { icon: 'clear', text },
          });
        })
      ),
    { dispatch: false }
  );

  public destroyProfilePaymentCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfilePaymentCardsActions.DESTROY_PROFILE_PAYMENT_CARD),
      tap(() => {
        this.angulartics2Segment.eventTrack('Edit card - Delete button clicked', {});
      }),
      switchMap((action) => {
        const url = `${ApiEndpoint.MEMBER_PROFILE_PAYMENT_CARD}${action.payload}/`;

        return this.http
          .delete(url, {
            params: { online_use: 'true', offline_use: 'false' },
          })
          .pipe(
            map(() => new MemberProfilePaymentCardsActions.DestroyProfilePaymentCardSuccess(action.payload)),
            catchError((error) => of(new MemberProfilePaymentCardsActions.DestroyProfilePaymentCardFail(error)))
          );
      })
    )
  );

  public updateProfilePaymentCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfilePaymentCardsActions.UPDATE_PROFILE_PAYMENT_CARD),
      tap((action) => {
        if (!action.payload.defaultCardChange) {
          const params = {
            preferredCardCheckbox: action.payload.data.is_default_card ? 'Yes' : 'No',
            // Temporarily removing offline use tracking until we can save cards for offline use
            // securelyStoreCardCheckbox: action.payload.data.offline_use ? 'Yes' : 'No',
          };

          this.angulartics2Segment.eventTrack('Edit card - Submit button clicked', params);
        }
      }),
      withLatestFrom(this.store.select(getMemberProfilePaymentCardsFeatureState)),
      switchMap(([action, cards]) => {
        const url = `${ApiEndpoint.MEMBER_PROFILE_PAYMENT_CARD}${action.payload.id}/`;
        const payload = { ...action.payload.data };
        // Not changeable fields, not present in the edit form
        delete payload.card_holder_name;
        delete payload.card_number;

        return this.http.patch<IMemberProfilePaymentCard>(url, payload).pipe(
          map(() => {
            const originalCard = cards.find((card) => card.id === action.payload.id);
            const updatedCard = deepmerge(originalCard, payload);
            return new MemberProfilePaymentCardsActions.UpdateProfilePaymentCardSuccess(updatedCard);
          }),
          catchError(() => of(new MemberProfilePaymentCardsActions.UpdateProfilePaymentCardFail()))
        );
      })
    )
  );
}
